import { CloseOutlined, EditOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import type { Contract, ContractMeterLink } from "@features/contracts/api";
import ContractForm from "@features/contracts/components/ContractForm";
import ContractMeterTab from "@features/contracts/components/ContractMeterTab";
import { contractActionColumn, contractDataColumns } from "@features/contracts/config/columns";
import { useContractMutations } from "@features/contracts/hooks/useContractMutations";
import { PageLayoutWithHelp } from "@features/page-help";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import EnterpriseDrawer from "@shared/ui/enterprise-drawer";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { App as AntdApp, Button, Flex, Form, Tabs, Tooltip } from "antd";
import { useState } from "react";

const AdminContractsPage = () => {
  const { message } = AntdApp.useApp();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [meters, setMeters] = useState<ContractMeterLink[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [form] = Form.useForm();

  const handleClose = () => {
    setDrawerOpen(false);
    setIsEditing(false);
    setIsDirty(false);
  };

  const { contractsQuery, isPending, create, update, remove } = useContractMutations(handleClose);

  const openCreate = () => {
    setEditing(null);
    setMeters([]);
    setIsEditing(true);
    setIsDirty(false);
    setDrawerOpen(true);
  };

  const openEdit = (record: Contract) => {
    setEditing(record);
    setMeters(record.meters ?? []);
    setIsEditing(false);
    setIsDirty(false);
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = editing && !isEditing ? form.getFieldsValue() : await form.validateFields();
      const payload = {
        ...values,
        start_date: values.start_date?.toISOString(),
        end_date: values.end_date?.toISOString(),
        meters,
      };
      if (editing) {
        update(editing.id, payload);
      } else {
        create(payload);
      }
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  const columns = [...contractDataColumns, contractActionColumn(openEdit, remove)];

  const isNew = !editing;

  return (
    <PageLayoutWithHelp
      title="Договора"
      extra={
        <Tooltip title="Создать договор">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Создать
          </Button>
        </Tooltip>
      }
    >
      <EnterpriseTable
        columns={columns}
        dataSource={contractsQuery.data ?? []}
        rowKey="id"
        loading={contractsQuery.isLoading}
        size="small"
        onRow={(record) => ({ onDoubleClick: () => openEdit(record) })}
      />
      <EnterpriseDrawer
        title={editing ? `Договор ${editing.contract_number}` : "Новый договор"}
        open={drawerOpen}
        onClose={handleClose}
        size="large"
        destroyOnHidden
        actions={
          <Flex gap={8}>
            <Tooltip title="Отменить">
              <Button icon={<CloseOutlined />} onClick={handleClose} />
            </Tooltip>
            {!isNew && !isEditing && !isDirty ? (
              <Tooltip title="Редактировать">
                <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)} />
              </Tooltip>
            ) : (
              <Tooltip title="Сохранить">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSubmit}
                  loading={isPending}
                  disabled={!isNew && !isDirty}
                />
              </Tooltip>
            )}
          </Flex>
        }
      >
        <Tabs
          defaultActiveKey="params"
          items={[
            {
              key: "params",
              label: "Параметры",
              children: (
                <ContractForm
                  form={form}
                  initialData={editing}
                  disabled={!isNew && !isEditing}
                  onValuesChange={() => setIsDirty(true)}
                />
              ),
            },
            {
              key: "meters",
              label: `Счётчики (${meters.length})`,
              children: (
                <ContractMeterTab
                  meters={meters}
                  onMetersChange={setMeters}
                  onDirty={() => setIsDirty(true)}
                />
              ),
            },
          ]}
        />
      </EnterpriseDrawer>
    </PageLayoutWithHelp>
  );
};

export default AdminContractsPage;
